// Enhanced user data cache for reducing redundant database queries
import { supabase } from './supabase';
import { createLogger } from './logger';
import { User } from '@supabase/supabase-js';

const logger = createLogger({ module: 'UserDataCache', level: 'info' });

export interface EnhancedUserData {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  user_type: string;
  onboarding_complete: boolean;
  location: string | null;
  professional_summary: string | null;
  skills: string[] | null;
  career_objectives: string | null;
  job_title: string | null;
  company: string | null;
  created_at: string;
  updated_at: string;
}

interface CacheEntry {
  data: EnhancedUserData;
  timestamp: number;
  expiresAt: number;
}

class UserDataCache {
  private cache = new Map<string, CacheEntry>();
  private pendingFetches = new Map<string, Promise<EnhancedUserData | null>>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes
  private readonly adminCheckTTL = 10 * 60 * 1000; // 10 minutes for admin checks
  
  constructor() {
    // Clear expired entries every minute
    setInterval(() => this.clearExpired(), 60 * 1000);
  }
  
  private clearExpired() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt < now) {
        this.cache.delete(key);
        logger.debug('Cleared expired user data cache entry', { userId: key });
      }
    }
  }
  
  /**
   * Get user data with automatic deduplication and caching
   */
  async getUserData(userId: string, options?: { 
    fresh?: boolean; 
    fields?: string[];
  }): Promise<EnhancedUserData | null> {
    // Return null for invalid userId
    if (!userId) return null;
    
    // Check if fresh data is required
    if (!options?.fresh) {
      const cached = this.getCached(userId);
      if (cached) {
        logger.debug('Returning cached user data', { 
          userId, 
          cacheAge: Date.now() - cached.timestamp 
        });
        return cached.data;
      }
    }
    
    // Check if there's already a pending fetch
    const pending = this.pendingFetches.get(userId);
    if (pending) {
      logger.debug('Returning pending fetch for user data', { userId });
      return pending;
    }
    
    // Create new fetch with deduplication
    const fetchPromise = this.fetchUserData(userId, options?.fields);
    this.pendingFetches.set(userId, fetchPromise);
    
    try {
      const data = await fetchPromise;
      return data;
    } finally {
      this.pendingFetches.delete(userId);
    }
  }
  
  private getCached(userId: string): CacheEntry | null {
    const entry = this.cache.get(userId);
    if (!entry) return null;
    
    if (entry.expiresAt < Date.now()) {
      this.cache.delete(userId);
      return null;
    }
    
    return entry;
  }
  
  private async fetchUserData(userId: string, fields?: string[]): Promise<EnhancedUserData | null> {
    try {
      const selectFields = fields?.join(',') || '*';
      const startTime = Date.now();
      
      const { data, error } = await supabase
        .from('users')
        .select(selectFields)
        .eq('id', userId)
        .single();
      
      const duration = Date.now() - startTime;
      logger.debug('Fetched user data from database', { 
        userId, 
        duration, 
        fields: selectFields 
      });
      
      if (error) {
        logger.error('Error fetching user data', { 
          userId, 
          error: error.message,
          code: error.code 
        });
        return null;
      }
      
      if (data) {
        // Cache the complete data
        const ttl = data.user_type === 'admin' ? this.adminCheckTTL : this.defaultTTL;
        this.setCache(userId, data as EnhancedUserData, ttl);
        return data as EnhancedUserData;
      }
      
      return null;
    } catch (error) {
      logger.error('Exception fetching user data', { 
        userId, 
        error: error instanceof Error ? error.message : String(error) 
      });
      return null;
    }
  }
  
  private setCache(userId: string, data: EnhancedUserData, ttl?: number) {
    const timestamp = Date.now();
    const expiresAt = timestamp + (ttl || this.defaultTTL);
    
    this.cache.set(userId, {
      data,
      timestamp,
      expiresAt
    });
    
    logger.debug('Cached user data', { 
      userId, 
      ttl: ttl || this.defaultTTL,
      fields: Object.keys(data).length 
    });
  }
  
  /**
   * Invalidate cached data for a user
   */
  invalidate(userId: string) {
    this.cache.delete(userId);
    logger.debug('Invalidated user data cache', { userId });
  }
  
  /**
   * Prefetch user data for multiple users
   */
  async prefetchUsers(userIds: string[]) {
    const uncachedIds = userIds.filter(id => !this.getCached(id));
    
    if (uncachedIds.length === 0) {
      logger.debug('All users already cached', { count: userIds.length });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .in('id', uncachedIds);
      
      if (error) {
        logger.error('Error prefetching users', { 
          error: error.message,
          count: uncachedIds.length 
        });
        return;
      }
      
      if (data) {
        data.forEach(user => {
          this.setCache(user.id, user as EnhancedUserData);
        });
        
        logger.debug('Prefetched user data', { 
          requested: uncachedIds.length,
          fetched: data.length 
        });
      }
    } catch (error) {
      logger.error('Exception prefetching users', { 
        error: error instanceof Error ? error.message : String(error) 
      });
    }
  }
  
  /**
   * Update specific fields in the cache
   */
  updateCachedFields(userId: string, updates: Partial<EnhancedUserData>) {
    const cached = this.getCached(userId);
    if (cached) {
      cached.data = { ...cached.data, ...updates };
      logger.debug('Updated cached user fields', { 
        userId, 
        fields: Object.keys(updates) 
      });
    }
  }
  
  /**
   * Get specific field from user data
   */
  async getUserField<K extends keyof EnhancedUserData>(
    userId: string, 
    field: K
  ): Promise<EnhancedUserData[K] | null> {
    const userData = await this.getUserData(userId);
    return userData ? userData[field] : null;
  }
  
  /**
   * Check if user is admin (optimized for frequent calls)
   */
  async isUserAdmin(userId: string): Promise<boolean> {
    const userType = await this.getUserField(userId, 'user_type');
    return userType === 'admin';
  }
  
  /**
   * Get onboarding status (optimized for auth flow)
   */
  async getOnboardingStatus(userId: string): Promise<boolean> {
    const status = await this.getUserField(userId, 'onboarding_complete');
    return status === true;
  }
  
  /**
   * Subscribe to auth changes to keep cache in sync
   */
  subscribeToAuthChanges() {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear all cache on sign out
        this.cache.clear();
        logger.debug('Cleared all user data cache on sign out');
      } else if (event === 'USER_UPDATED' && session?.user) {
        // Invalidate cache on user update
        this.invalidate(session.user.id);
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }
}

// Export singleton instance
export const userDataCache = new UserDataCache();